import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes, css } from 'styled-components';
import { FaBoxOpen, FaBarcode, FaGift, FaSearch, FaCalendarAlt, FaCopy, FaCheck, FaTrophy } from 'react-icons/fa';
import { theme, animations } from '../../styles/theme';
import ApiService from '../../services/ApiService';
import { useAuth } from '../../hooks/useApi';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Helper to resolve media URL
const getMediaUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http') || url.startsWith('//')) return url;
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  return `${backendUrl.replace(/\/$/, '')}${url}`;
};

const sparkle = keyframes`
  0%, 100% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
`;

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const InventoryContainer = styled.div`
  min-height: 100vh;
  background: ${theme.gradients.background};
  position: relative;
  overflow: hidden;




&::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(circle at 20% 80%, rgba(0, 0, 0, 0.1) 0%, transparent 50%),
                      radial-gradient(circle at 80% 20%, rgba(0, 0, 0, 0.1) 0%, transparent 50%),
                      radial-gradient(circle at 40% 40%, rgba(0, 0, 0, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(circle at 2% 4%, rgba(255, 255, 255, 0.26) 1px, transparent 0),
                      radial-gradient(circle at 29% 36%, rgba(255, 255, 255, 0.32) 1px, transparent 0),
                      radial-gradient(circle at 50% 60%, rgba(255, 255, 255, 0.2) 1px, transparent 0),
                      radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.66) 1px, transparent 0),
                      radial-gradient(circle at 70% 10%, rgba(255, 255, 255, 0.11) 1px, transparent 0),
                      radial-gradient(circle at 60% 12%, rgba(255, 255, 255, 0.29) 1px, transparent 0),
                      radial-gradient(circle at 43% 90%, rgba(255, 255, 255, 0.51) 1px, transparent 0),
                      radial-gradient(circle at 32% 83%, rgba(255, 255, 255, 0.16) 1px, transparent 0),
                      radial-gradient(circle at 49% 53%, rgba(255, 255, 255, 0.6) 1px, transparent 0),
                      radial-gradient(circle at 89% 52%, rgba(255, 255, 255, 0.14) 1px, transparent 0);

    
    background-size: 25% 25%;
    pointer-events: none;
    background-repeat: repeat;
    animation: ${sparkle} 4s ease-in-out infinite;

  }
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${theme.spacing.xl};
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    padding: ${theme.spacing.md};
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing.xxl};
  animation: ${fadeIn} 0.8s ease-out;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    margin-bottom: ${theme.spacing.xl};
  }
  
  @media (max-width: 480px) {
    margin-bottom: ${theme.spacing.lg};
  }
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
`;

const Title = styled.h1`
  margin: 0 0 ${theme.spacing.xs} 0;
  font-size: 2rem;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  background: ${theme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: ${theme.typography.fontWeights.bold};
`;

const Subtitle = styled.p`
  color: ${theme.colors.textSecondary};
  margin: 0;
  font-size: 1rem;
`;

const StatsContainer = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  flex-wrap: wrap;
  justify-content: center;
  margin-top: ${theme.spacing.md};

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    width: 100%;
  }
`;

const StatCard = styled.div`
  background: rgba(20, 20, 35, 0);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 215, 0, 0.2);
  border-radius: ${theme.borderRadius.medium};
  padding: ${theme.spacing.md};
  min-width: 100px;
  text-align: center;
  flex: 1;

  @media (max-width: 768px) {
    min-width: 90px;
    padding: ${theme.spacing.sm};
  }

  @media (max-width: 480px) {
    min-width: unset;
    width: 100%;
  }
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: ${theme.colors.primary};
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: ${theme.colors.textMuted};
`;

const Controls = styled.div`
  display: flex;
  justify-content: center;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.xxl};
  overflow-x: auto;
  padding-bottom: ${theme.spacing.lg};
  animation: ${fadeIn} 0.8s ease-out 0.2s both;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    margin-bottom: ${theme.spacing.xl};
  }
`;

const SearchInput = styled.div`
  position: relative;
  flex: 1;
  min-width: 250px;

  input {
    width: 100%;
    padding: ${theme.spacing.sm} ${theme.spacing.lg};
    padding-left: 40px;
    background: ${theme.colors.cardBackground};
    border: 1px solid ${theme.colors.backgroundLight};
    border-radius: ${theme.borderRadius.large};
    font-size: 1rem;
    color: ${theme.colors.textPrimary};
    outline: none;
    transition: all 0.3s ease;
    box-sizing: border-box;

    &:focus {
      border-color: ${theme.colors.inputFocus};
      background: rgba(30, 30, 60, 0.7);
    }
    
    &::placeholder {
      color: rgba(255, 255, 255, 0.4);
    }
  }

  svg {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: ${theme.colors.textMuted};
  }
`;

const FilterButton = styled.button`
  padding: 10px 20px;
  border: 1px solid ${props => props.active ? theme.colors.primary : 'rgba(255, 255, 255, 0.1)'};
  background: ${props => props.active ? 'rgba(255, 215, 0, 0.1)' : 'rgba(255, 255, 255, 0.05)'};
  color: ${props => props.active ? theme.colors.primary : theme.colors.textSecondary};
  border-radius: ${theme.borderRadius.medium};
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 215, 0, 0.15);
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${theme.spacing.lg};
`;

const mythicAnim = keyframes`
  0% { border-color: #ff0000; box-shadow: 0 0 5px rgba(255, 0, 0, 0.3); }
  20% { border-color: #ffff00; box-shadow: 0 0 5px rgba(255, 255, 0, 0.3); }
  40% { border-color: #00ff00; box-shadow: 0 0 5px rgba(0, 255, 0, 0.3); }
  60% { border-color: #00ffff; box-shadow: 0 0 5px rgba(0, 255, 255, 0.3); }
  80% { border-color: #0000ff; box-shadow: 0 0 5px rgba(0, 0, 255, 0.3); }
  100% { border-color: #ff00ff; box-shadow: 0 0 5px rgba(255, 0, 255, 0.3); }
`;

const legendaryAnim = keyframes`
  0% { border-color: #FFD700; box-shadow: 0 0 4px rgba(255, 215, 0, 0.2); }
  50% { border-color: #FFF000; box-shadow: 0 0 8px rgba(255, 215, 0, 0.4); }
  100% { border-color: #FFD700; box-shadow: 0 0 4px rgba(255, 215, 0, 0.2); }
`;

const epicAnim = keyframes`
  0% { border-color: #A335EE; box-shadow: 0 0 4px rgba(163, 53, 238, 0.2); }
  50% { border-color: #D866FF; box-shadow: 0 0 8px rgba(163, 53, 238, 0.4); }
  100% { border-color: #A335EE; box-shadow: 0 0 4px rgba(163, 53, 238, 0.2); }
`;

const rareAnim = keyframes`
  0% { border-color: #0070DD; box-shadow: 0 0 4px rgba(0, 112, 221, 0.2); }
  50% { border-color: #4DA6FF; box-shadow: 0 0 8px rgba(0, 112, 221, 0.4); }
  100% { border-color: #0070DD; box-shadow: 0 0 4px rgba(0, 112, 221, 0.2); }
`;

const getRarityAnimation = (rarity) => {
  switch(rarity) {
    case 'Mythic': return mythicAnim;
    case 'Legendary': return legendaryAnim;
    case 'Epic': return epicAnim;
    case 'Rare': return rareAnim;
    default: return null;
  }
};

const getRarityColor = (rarity) => {
  switch(rarity) {
    case 'Mythic': return '#FF0055';
    case 'Legendary': return '#FFD700';
    case 'Epic': return '#A335EE';
    case 'Rare': return '#0070DD';
    case 'Uncommon': return '#1EFF00';
    default: return 'rgb(255, 255, 255)';
  }
};

const ItemCard = styled.div`
  background: rgba(12, 12, 22, 0.72);
  backdrop-filter: blur(14px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: ${theme.borderRadius.large};
  overflow: hidden;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  display: flex;
  flex-direction: column;
  position: relative;
  box-shadow: ${props => props.rarity ? `0 4px 24px ${getRarityColor(props.rarity)}28` : '0 2px 12px rgba(0,0,0,0.35)'};
  cursor: pointer;

  ${props => getRarityAnimation(props.rarity) && css`
    animation: ${getRarityAnimation(props.rarity)} ${props.rarity === 'Mythic' ? '3s' : props.rarity === 'Legendary' ? '2s' : props.rarity === 'Epic' ? '3s' : '4s'} ${props.rarity === 'Mythic' ? 'linear' : 'ease-in-out'} infinite;
    ${props.rarity === 'Mythic' && 'border-width: 2px;'}
  `}

  &:hover {
    transform: translateY(-3px);
    box-shadow: ${props => props.rarity ? `0 8px 32px ${getRarityColor(props.rarity)}40` : '0 8px 28px rgba(0,0,0,0.5)'};
  }
`;

const ItemHeader = styled.div`
  padding: ${theme.spacing.md};
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(0, 0, 0, 0);
`;

const ItemType = styled.span`
  font-size: 0.75rem;
  font-weight: bold;
  text-transform: uppercase;
  padding: 4px 8px;
  border-radius: 4px;
  background: ${props => props.type === 'gift_card' ? 'rgba(255, 140, 0, 0.2)' : props.type === 'game_item' ? 'rgba(255, 215, 0, 0.2)' : 'rgba(0, 215, 255, 0.2)'};
  color: ${props => props.type === 'gift_card' ? '#FF8C00' : props.type === 'game_item' ? '#FFD700' : '#00D7FF'};
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    color: inherit;
  }
`;

const ItemDate = styled.span`
  font-size: 0.875rem;
  color: ${theme.colors.textMuted};
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ItemBody = styled.div`
  padding: ${theme.spacing.md};
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const GiftCardCover = styled.div`
  width: 100%;
  height: 170px;
  background: rgba(255, 255, 255, 0.03);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const GiftCardCoverImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const GameItemImageArea = styled.div`
  width: 100%;
  height: 180px;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;

  img, video {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const RarityBadge = styled.span`
  position: absolute;
  bottom: 8px;
  left: 8px;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  background: ${props => props.color || 'rgba(255,255,255,0.15)'};
  color: ${props => props.textColor || '#fff'};
  backdrop-filter: blur(4px);
  border: 1px solid ${props => props.color ? props.color + '80' : 'rgba(255,255,255,0.2)'};
`;

const ItemTitle = styled.h3`
  margin: 0;
  color: ${theme.colors.textPrimary};
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.3;
`;

const ItemSubtitle = styled.p`
  margin: 0;
  color: ${theme.colors.textSecondary};
  font-size: 0.85rem;
`;

const CodeSection = styled.div`
  background: rgba(0, 0, 0, 0.3);
  padding: ${theme.spacing.sm};
  border-radius: 6px;
  border: 1px dashed rgba(255, 255, 255, 0.2);
  margin-top: ${theme.spacing.sm};
  font-family: monospace;
  font-size: 0.9rem;
  color: ${theme.colors.primary};
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 215, 0, 0.1);
    border-color: ${theme.colors.primary};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing.xxl};
  color: ${theme.colors.textMuted};
  
  svg {
    font-size: 4rem;
    margin-bottom: ${theme.spacing.md};
    color: rgba(255, 255, 255, 0.1);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: ${theme.spacing.md};
  animation: ${animations.fadeIn} 0.2s ease-out;
`;

const ModalContent = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: ${theme.borderRadius.large};
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5);
  animation: ${animations.slideUp} 0.3s ease-out;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 10;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: rotate(90deg);
  }
`;

const RevealButton = styled.button`
  width: 100%;
  padding: 12px 24px;
  background: ${props => props.disabled ? 'rgba(255, 255, 255, 0.1)' : theme.gradients.primary};
  border: 1px solid ${theme.colors.primary};
  color: ${props => props.disabled ? theme.colors.textMuted : '#000'};
  border-radius: ${theme.borderRadius.medium};
  font-size: 1rem;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &:hover {
    transform: ${props => props.disabled ? 'none' : 'translateY(-2px)'};
    box-shadow: ${props => props.disabled ? 'none' : '0 4px 12px rgba(255, 215, 0, 0.3)'};
  }
  
  &:active {
    transform: ${props => props.disabled ? 'none' : 'translateY(0)'};
  }
`;

const EmailButton = styled(RevealButton)`
  background: ${props =>
    props.disabled
      ? 'rgba(255, 255, 255, 0.08)'
      : 'linear-gradient(135deg, rgba(103, 58, 183, 0.95), rgba(33, 150, 243, 0.9))'};
  border-color: ${props => (props.disabled ? 'rgba(255, 255, 255, 0.18)' : 'rgba(186, 104, 200, 0.9)')};
  color: ${props => (props.disabled ? theme.colors.textMuted : '#fff')};

  &:hover {
    box-shadow: ${props =>
      props.disabled
        ? 'none'
        : '0 8px 22px rgba(103, 58, 183, 0.35), 0 6px 16px rgba(33, 150, 243, 0.25)'};
  }
`;

const CodeRevealSection = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 215, 0, 0.2);
  border-radius: ${theme.borderRadius.medium};
  padding: 16px;
  margin-top: 12px;
`;

const CodeItem = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: ${theme.borderRadius.small};
  padding: 12px;
  margin-bottom: 12px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;
`;

const ModalHeader = styled.div`
  padding: ${theme.spacing.xl};
  background: linear-gradient(to bottom, rgba(0,0,0,0.2), transparent);
  text-align: center;
`;

const ModalBody = styled.div`
  padding: ${theme.spacing.xl};
`;

const ModalImageContainer = styled.div`
  width: 100%;
  height: 300px;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.medium};
  overflow: hidden;
`;

const ModalTitle = styled.h2`
  font-size: 2rem;
  margin: 0 0 ${theme.spacing.sm} 0;
  color: ${props => props.color || theme.colors.textPrimary};
  text-shadow: 0 2px 10px rgba(0,0,0,0.5);
`;

const ModalSubtitle = styled.p`
  font-size: 1.1rem;
  color: ${theme.colors.textSecondary};
  margin: 0;
`;

const ModalSection = styled.div`
  margin-bottom: ${theme.spacing.lg};
  padding-bottom: ${theme.spacing.lg};
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const ModalLabel = styled.h4`
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: ${theme.colors.textMuted};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

const ModalText = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: ${theme.colors.textPrimary};
  margin: 0;
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: ${theme.spacing.md};
`;

const StatBox = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.medium};
  text-align: center;
`;

const Inventory = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, scan, gift_card
  const [search, setSearch] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [revealedCodes, setRevealedCodes] = useState({}); // Store revealed codes by orderName
  const [revealingOrder, setRevealingOrder] = useState(null); // Track which order is being revealed
  const [sendingEmail, setSendingEmail] = useState(null); // Track which order is sending email

  const formatDate = (value) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
  };

  const formatDateTime = (value) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '—' : `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
  };

  // Lock body scroll when modal is open
  useEffect(() => {
    if (selectedItem) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [selectedItem]);

  useEffect(() => {
    const fetchInventory = async () => {
      if (!user) return;
      try {
        setLoading(true);
        const response = await ApiService.getUserInventory();
        if (response && response.data) {
          // Transform redemption data to match expected format
          const transformedItems = response.data.map(redemption => ({
            ...redemption,
            date: (
              redemption.createdAt ||
              redemption.createdAtIso ||
              redemption.attributes?.createdAt ||
              redemption.attributes?.created_at ||
              redemption.orderDate ||
              redemption.date ||
              redemption.details?.createdAt ||
              redemption.details?.created_at ||
              redemption.created_at ||
              null
            ),
            title: redemption.title || redemption.productName || 'Unknown Product',
            subtitle: redemption.subtitle || (redemption.orderName ? `Order: ${redemption.orderName}` : ''),
            type: redemption.type || 'gift_card',
            image: redemption.image || redemption.productImage || redemption.coverImage || null,
            points: (redemption.type === 'game_item'
              ? (typeof redemption.points === 'number' ? redemption.points : 0)
              : (typeof redemption.pointsCost === 'number'
                ? -redemption.pointsCost
                : (typeof redemption.points === 'number' ? redemption.points : 0))),
            details: (redemption.type === 'game_item')
              ? (redemption.details || {})
              : {
                orderName: redemption.orderName,
                pointsCost: redemption.pointsCost,
                quantity: redemption.quantity,
                status: redemption.status,
                createdAt: redemption.createdAt,
              }
          }));
          setItems(transformedItems);
        }
      } catch (error) {
        console.error('Failed to load inventory:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInventory();
  }, [user]);

  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleRevealCode = async (orderName) => {
    if (!orderName) {
      console.error('Missing orderName:', { orderName });
      return;
    }
    
    // If already revealed, do nothing
    if (revealedCodes[orderName]) {
      return;
    }

    try {
      setRevealingOrder(orderName);
      console.log('🔍 Fetching order details for:', orderName);
      const response = await ApiService.getGiftCardOrderDetails(orderName);
      
      console.log('✅ Order details response:', response);
      
      if (response && response.data) {
        // Store the revealed codes
        setRevealedCodes(prev => ({
          ...prev,
          [orderName]: response.data
        }));
      } else {
        console.error('Invalid response structure:', response);
        alert('Unexpected response format from server.');
      }
    } catch (error) {
      console.error('❌ Failed to reveal codes:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data
      });
      
      const errorMsg = error.response?.data?.error?.message || error.message || 'Failed to load gift card codes';
      alert(`Error: ${errorMsg}`);
    } finally {
      setRevealingOrder(null);
    }
  };

  const handleSendToEmail = async (orderName) => {
    if (!orderName) {
      console.error('Missing orderName:', { orderName });
      return;
    }

    try {
      setSendingEmail(orderName);
      await ApiService.emailGiftCardCodes(orderName);
      alert('✅ Gift card codes sent to your email.');
    } catch (error) {
      console.error('❌ Failed to send email:', error);
      
      const errorMsg = error.response?.data?.error?.message || error.message || 'Failed to send email';
      alert(`Error: ${errorMsg}`);
    } finally {
      setSendingEmail(null);
    }
  };

  const handleItemClick = (item) => {
    setSelectedItem(item);
  };

  const filteredItems = items.filter(item => {
    const matchesFilter = filter === 'all' || item.type === filter;
    const title = item.title || item.productName || '';
    const subtitle = item.subtitle || '';
    const matchesSearch = title.toLowerCase().includes(search.toLowerCase()) || 
                          subtitle.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const stats = {
    total: items.length,
    scans: items.filter(i => i.type === 'scan').length,
    giftCards: items.filter(i => i.type === 'gift_card').length,
    gameItems: items.filter(i => i.type === 'game_item').length
  };

  if (loading) {
    return (
      <InventoryContainer>
        <ContentWrapper>
          <div style={{ textAlign: 'center', padding: '40px', color: theme.colors.textMuted }}>
            Loading inventory...
          </div>
        </ContentWrapper>
      </InventoryContainer>
    );
  }

  return (
    <InventoryContainer>
      <ContentWrapper>
        <Header>
          <TitleSection>
            <Title>My Inventory</Title>
            <Subtitle>Manage your scanned items and rewards</Subtitle>
          </TitleSection>
          <StatsContainer>
            <StatCard>
              <StatValue>{stats.total}</StatValue>
              <StatLabel>Total Items</StatLabel>
            </StatCard>
            {/* <StatCard>
              <StatValue>{stats.scans}</StatValue>
              <StatLabel>Scans</StatLabel>
            </StatCard> */}
            <StatCard>
              <StatValue>{stats.giftCards}</StatValue>
              <StatLabel>Gift Cards</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{stats.gameItems}</StatValue>
              <StatLabel>Trophies</StatLabel>
            </StatCard>
          </StatsContainer>
        </Header>

        <Controls>
          <SearchInput>
            <FaSearch />
            <input 
              type="text" 
              placeholder="Search items..." 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </SearchInput>
          <FilterButton active={filter === 'all'} onClick={() => setFilter('all')}>
            All Items
          </FilterButton>
           <FilterButton active={filter === 'game_item'} onClick={() => setFilter('game_item')}>
            <FaTrophy /> Trophies
          </FilterButton>
          
          <FilterButton active={filter === 'gift_card'} onClick={() => setFilter('gift_card')}>
            <FaGift /> Gift Cards
          </FilterButton>
         <FilterButton active={filter === 'scan'} onClick={() => setFilter('scan')}>
            <FaBoxOpen /> Items
          </FilterButton>
        </Controls>

        {filteredItems.length > 0 ? (
          <Grid>
            {filteredItems.map(item => (
              <ItemCard
                key={item.id}
                itemType={item.type}
                rarity={item.details?.rarity}
                onClick={() => handleItemClick(item)}
              >
                <ItemHeader type={item.type}>
                  <ItemType type={item.type}>
                    {item.type === 'scan' ? <FaBarcode /> : item.type === 'gift_card' ? <FaGift /> : <FaTrophy />}
                    {item.type === 'scan' ? 'Scan' : item.type === 'gift_card' ? 'Reward' : 'Trophy'}
                  </ItemType>
                  <ItemDate>
                    <FaCalendarAlt />
                    {formatDate(item.date)}
                  </ItemDate>
                </ItemHeader>

                {item.type === 'gift_card' && item.image && (
                  <GiftCardCover>
                    <GiftCardCoverImage src={getMediaUrl(item.image)} alt={item.title} loading="lazy" />
                  </GiftCardCover>
                )}

                {item.type === 'game_item' && item.image && (
                  <GameItemImageArea>
                    {item.mime && item.mime.startsWith('video/') ? (
                      <video src={getMediaUrl(item.image)} autoPlay loop muted playsInline />
                    ) : (
                      <img src={getMediaUrl(item.image)} alt={item.title} loading="lazy" />
                    )}
                    {item.details?.rarity && (
                      <RarityBadge
                        color={getRarityColor(item.details.rarity) === 'rgb(255, 255, 255)' ? 'rgba(255,255,255,0.18)' : getRarityColor(item.details.rarity) + '33'}
                        textColor={getRarityColor(item.details.rarity)}
                      >
                        {item.details.rarity}
                      </RarityBadge>
                    )}
                  </GameItemImageArea>
                )}

                <ItemBody>
                  <ItemTitle>{item.title}</ItemTitle>
                  {item.subtitle ? <ItemSubtitle>{item.subtitle}</ItemSubtitle> : null}

                  {item.type === 'game_item' && (item.details?.codeName || item.details?.sn) && (
                    <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {item.details.codeName && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: theme.colors.textMuted, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Code</span>
                          <span style={{ color: theme.colors.primary, fontWeight: '600', fontSize: '0.85rem', fontFamily: 'monospace' }}>{item.details.codeName}</span>
                        </div>
                      )}
                      {item.details.sn && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: theme.colors.textMuted, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>S/N</span>
                          <span style={{ color: theme.colors.textSecondary, fontSize: '0.8rem', fontFamily: 'monospace' }}>{item.details.sn}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {item.type === 'gift_card' && Number.isFinite(item.details?.pointsCost) && (
                    <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: theme.colors.textMuted, fontSize: '0.85rem' }}>Spent</span>
                      <span style={{ color: theme.colors.error, fontWeight: '600', fontSize: '0.95rem' }}>
                        -{(Number(item.details?.pointsCost) || 0) * (Number(item.details?.quantity) || 1)} <img src='images/ggcoin.svg' alt='GG Coin' style={{ width: '14px', height: '14px', verticalAlign: 'text-bottom' }} />
                      </span>
                    </div>
                  )}

                  {item.type === 'scan' && (
                    <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: theme.colors.textMuted, fontSize: '0.85rem' }}>Earned</span>
                      <span style={{ color: theme.colors.primary, fontWeight: '600', fontSize: '0.95rem' }}>+{item.points} <img src='images/ggcoin.svg' alt='GG Coin' style={{ width: '14px', height: '14px', verticalAlign: 'text-bottom' }} /></span>
                    </div>
                  )}
                </ItemBody>
              </ItemCard>
            ))}
          </Grid>
        ) : (
          <EmptyState>
            <FaBoxOpen />
            <h3>No items found</h3>
            <p>Try adjusting your search or filters</p>
          </EmptyState>
        )}

        {selectedItem && ReactDOM.createPortal(
          <ModalOverlay
            role="dialog"
            aria-modal="true"
            aria-label={selectedItem.title}
            onClick={() => setSelectedItem(null)}
          >
            <ModalContent onClick={e => e.stopPropagation()}>
              <CloseButton aria-label="Close" onClick={() => setSelectedItem(null)}>×</CloseButton>
              <ModalHeader>
                <ModalTitle color={selectedItem.details?.rarity ? getRarityColor(selectedItem.details.rarity) : null}>
                  {selectedItem.title}
                </ModalTitle>
                <ModalSubtitle>{selectedItem.subtitle}</ModalSubtitle>
              </ModalHeader>
              <ModalBody>
                {selectedItem.image && (
                  <ModalImageContainer>
                    {selectedItem.mime && selectedItem.mime.startsWith('video/') ? (
                      <video
                        src={getMediaUrl(selectedItem.image)}
                        autoPlay
                        loop
                        muted
                        playsInline
                        controls
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      />
                    ) : (
                      <img
                        src={getMediaUrl(selectedItem.image)}
                        alt={selectedItem.title}
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      />
                    )}
                  </ModalImageContainer>
                )}
                {selectedItem.type === 'game_item' && (
                  <>
                    {selectedItem.details.story && (
                      <ModalSection>
                        <ModalLabel>Story</ModalLabel>
                        <ModalText>{selectedItem.details.story}</ModalText>
                      </ModalSection>
                    )}
                    {selectedItem.details.stats && (
                      <ModalSection>
                        <ModalLabel>Stats</ModalLabel>
                        <StatGrid>
                          {Object.entries(selectedItem.details.stats).map(([key, value]) => (
                            <StatBox key={key}>
                              <div style={{ color: theme.colors.primary, fontSize: '1.2rem', fontWeight: 'bold' }}>{value}</div>
                              <div style={{ fontSize: '0.8rem', color: theme.colors.textMuted, textTransform: 'uppercase' }}>{key}</div>
                            </StatBox>
                          ))}
                        </StatGrid>
                      </ModalSection>
                    )}
                  </>
                )}
                <ModalSection>
                  <ModalLabel>Details</ModalLabel>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <div style={{ color: theme.colors.textMuted, fontSize: '0.8rem' }}>Date Acquired</div>
                      <div style={{ color: 'white' }}>{formatDateTime(selectedItem.date)}</div>
                    </div>
                    <div>
                      <div style={{ color: theme.colors.textMuted, fontSize: '0.8rem' }}>Type</div>
                      <div
                        style={{
                          textTransform: 'capitalize',
                          color: selectedItem.details?.rarity ? getRarityColor(selectedItem.details.rarity) : theme.colors.textPrimary,
                        }}
                      >
                        {selectedItem.type.replace('_', ' ')}
                      </div>
                    </div>
                    {selectedItem.type === 'gift_card' && Number.isFinite(selectedItem.details?.pointsCost) && (
                      <div>
                        <div style={{ color: theme.colors.textMuted, fontSize: '0.8rem' }}>Spent</div>
                        <div style={{ color: theme.colors.error }}>
                          -{(Number(selectedItem.details?.pointsCost) || 0) * (Number(selectedItem.details?.quantity) || 1)} <img src='images/ggcoin.svg' alt='GG Coin' style={{ width: '14px', height: '14px', verticalAlign: 'text-bottom' }} />
                        </div>
                      </div>
                    )}
                    {selectedItem.type !== 'gift_card' && Number.isFinite(selectedItem.points) && selectedItem.points !== 0 && (
                      <div>
                        <div style={{ color: theme.colors.textMuted, fontSize: '0.8rem' }}>Value</div>
                        <div style={{ color: selectedItem.points > 0 ? theme.colors.success : theme.colors.error }}>
                          {selectedItem.points > 0 ? '+' : ''}{selectedItem.points} <img src='images/ggcoin.svg' alt='GG Coin' style={{ width: '14px', height: '14px', verticalAlign: 'text-bottom' }} />
                        </div>
                      </div>
                    )}
                  </div>
                </ModalSection>
                {selectedItem.type === 'gift_card' && (
                  <ModalSection>
                    <ModalLabel>Redemption Information</ModalLabel>
                    
                    {/* All gift cards use unified reveal button pattern */}
                    {selectedItem.details.orderName && (
                      <>
                        <div style={{ marginBottom: '16px', padding: '12px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}>
                          <div style={{ fontSize: '0.8rem', color: theme.colors.textMuted }}>Order ID: {selectedItem.details.orderName}</div>
                        </div>
                        
                        {/* Reveal Button */}
                        {!revealedCodes[selectedItem.details.orderName] && (
                          <>
                            <RevealButton 
                              onClick={() => handleRevealCode(selectedItem.details.orderName)}
                              disabled={revealingOrder === selectedItem.details.orderName}
                            >
                              {revealingOrder === selectedItem.details.orderName ? (
                                <>
                                  <Spinner />
                                  Loading...
                                </>
                              ) : (
                                <>
                                  <FaGift />
                                  Reveal Gift Card Code
                                </>
                              )}
                            </RevealButton>
                            
                            <EmailButton 
                              onClick={() => handleSendToEmail(selectedItem.details.orderName)}
                              disabled={sendingEmail === selectedItem.details.orderName}
                              style={{ marginTop: '10px' }}
                            >
                              {sendingEmail === selectedItem.details.orderName ? (
                                <>
                                  <Spinner />
                                  Sending...
                                </>
                              ) : (
                                <>
                                  📧 Send to Email
                                </>
                              )}
                            </EmailButton>
                          </>
                        )}
                        
                        {/* Display Revealed Codes */}
                        {revealedCodes[selectedItem.details.orderName] && (
                          <CodeRevealSection>
                            <div style={{ fontSize: '0.9rem', color: theme.colors.primary, fontWeight: '600', marginBottom: '12px' }}>
                              ✨ Gift Card Details
                            </div>
                            
                            {revealedCodes[selectedItem.details.orderName].orderData?.map((orderItem, idx) => (
                              <div key={idx}>
                                <div style={{ fontSize: '0.85rem', color: theme.colors.textMuted, marginBottom: '8px' }}>
                                  {orderItem.productName}
                                </div>
                                
                                {orderItem.serials?.map((serial, serialIdx) => (
                                  <CodeItem key={serialIdx}>
                                    {serial.serialCode && (
                                      <div style={{ marginBottom: '8px' }}>
                                        <div style={{ fontSize: '0.75rem', color: theme.colors.textMuted, marginBottom: '4px' }}>
                                          Code:
                                        </div>
                                        <CodeSection onClick={() => handleCopy(serial.serialCode, `revealed-${idx}-${serialIdx}-code`)}>
                                          <span style={{ fontFamily: 'monospace' }}>{serial.serialCode}</span>
                                          {copiedId === `revealed-${idx}-${serialIdx}-code` ? <FaCheck color={theme.colors.success} /> : <FaCopy />}
                                        </CodeSection>
                                      </div>
                                    )}
                                    
                                    {serial.serialNumber && (
                                      <div>
                                        <div style={{ fontSize: '0.75rem', color: theme.colors.textMuted, marginBottom: '4px' }}>
                                          Serial / PIN:
                                        </div>
                                        <CodeSection onClick={() => handleCopy(serial.serialNumber, `revealed-${idx}-${serialIdx}-serial`)}>
                                          <span style={{ fontFamily: 'monospace' }}>{serial.serialNumber}</span>
                                          {copiedId === `revealed-${idx}-${serialIdx}-serial` ? <FaCheck color={theme.colors.success} /> : <FaCopy />}
                                        </CodeSection>
                                      </div>
                                    )}
                                  </CodeItem>
                                ))}
                              </div>
                            ))}
                            
                            <div style={{ marginTop: '12px', fontSize: '0.75rem', color: theme.colors.textMuted, textAlign: 'center' }}>
                              💡 Click any code to copy to clipboard
                            </div>
                          </CodeRevealSection>
                        )}
                      </>
                    )}
                    
                    {!selectedItem.details.code && !selectedItem.details.orderName && (
                      <div style={{ color: theme.colors.textMuted, textAlign: 'center', padding: '20px' }}>
                        No redemption information available
                      </div>
                    )}
                  </ModalSection>
                )}
              </ModalBody>
            </ModalContent>
          </ModalOverlay>,
          document.body
        )}
      </ContentWrapper>
    </InventoryContainer>
  );
};

export default Inventory;
